import {
    MediaPauseEvent,
    MediaPlayer,
    MediaPlayerInstance,
    MediaPlayEvent,
    MediaPlayRequestEvent,
    MediaProvider,
    MediaSeekedEvent,
    TimeSliderInstance,
    useMediaRemote,
    useMediaStore,
} from '@vidstack/react';

import { PlyrLayout, plyrLayoutIcons } from '@vidstack/react/player/layouts/plyr';
import { useEffect, useRef } from 'react';
import { useVideoPlayerSync } from '../../state/VideoPlayerSync/VideoPlayerSyncContext';
import { Box } from '@mui/material';
import { FileUploadTypeEnum } from '../../generated/api';
import VideoTimeSliderControl from './Controls/VideoTimeSliderControl';
import { React } from 'Imports';

import '@vidstack/react/player/styles/base.css';
import '@vidstack/react/player/styles/plyr/theme.css';

interface VideoPlayerProps {
    src: string;
    title?: string;
    maxHeight?: number;
    videoType: FileUploadTypeEnum;
    forMobileView?: boolean;
    pauseVideoMobile?: boolean;
    activeVideoMobile?: boolean;
    isSwipingMobile?: boolean;
    onPlayMobile?: () => void;
    onEndMobile?: () => void;
}

const styles = require('./VideoPlayer.scss') as {
    videoContainer: string;
};

const VideoPlayer = (props: VideoPlayerProps) => {
    const playerRef = useRef<MediaPlayerInstance>(null);
    const sliderRef = useRef<TimeSliderInstance>(null);
    const isSeekingRef = useRef<boolean>(false);
    const isRestartingRef = useRef<boolean>(false);
    const latestSeekTimeRef = useRef<number>(0);
    const remote = useMediaRemote(playerRef);
    const {
        isPlayingGlobal,
        setIsPlayingGlobal,
        maxDurationGlobal,
        setMaxDurationGlobal,
        maxDurationVideoType,
        seekTimeGlobal,
        setSeekTimeGlobal,
        videosStarted,
        setVideosStarted,
        videosWatched,
        setVideosWatched,
        setVideoEnded,
        allVideosEnded,
        startPlayFromBeginning,
        setStartPlayFromBeginning,
    } = useVideoPlayerSync();

    const { paused, ended, currentTime, duration } = useMediaStore(playerRef);

    const isLocallyAtEndZone = (time: number): boolean => time >= Math.trunc(duration);
    const isGloballyAtEndZone = (time: number): boolean => time >= Math.trunc(maxDurationGlobal);

    useEffect(() => {
        const player = playerRef.current;
        if (!player || duration === 0) return;

        setMaxDurationGlobal(duration, props.videoType);

        return () => {
            if (!player || props.forMobileView) return;
            setSeekTimeGlobal(player.state.ended ? maxDurationGlobal : player.currentTime);
        };
    }, [duration]);

    useEffect(() => {
        const player = playerRef.current;

        return () => {
            // Force cleared video buffers for iOS memory safety
            if (player) {
                player.pause();

                const rootElement = player.el;
                const videoElement = rootElement?.querySelector ? rootElement.querySelector('video') : null;

                if (videoElement) {
                    videoElement.removeAttribute('src');
                    videoElement.load();
                }
            }
        };
    }, []);

    useEffect(() => {
        if (props.forMobileView && ended) {
            props.onEndMobile && props.onEndMobile();
            return;
        }
        setVideoEnded(props.videoType, ended);
    }, [ended, props.videoType, props.forMobileView]);

    useEffect(() => {
        if (props.pauseVideoMobile) {
            remote.pause();
        }
    }, [props.pauseVideoMobile]);

    useEffect(() => {
        if (props.activeVideoMobile) {
            remote.seek(0);
        }
    }, [props.activeVideoMobile]);

    // ------------ Sync Local -> Global ----------------
    const handleOnPlay = (nativeEvent: MediaPlayEvent) => {
        if (nativeEvent.isOriginTrusted) {
            if (isLocallyAtEndZone(currentTime + 0.15) && !allVideosEnded()) {
                remote.seek(0);
                setStartPlayFromBeginning(true);
                setIsPlayingGlobal(true);
            } else {
                setIsPlayingGlobal(true);
            }
        }
        if (!props.forMobileView && props.videoType === maxDurationVideoType && !videosStarted) {
            setVideosStarted(true);
        }

        if (props.forMobileView && props.onPlayMobile) {
            props.onPlayMobile();
        }
    };

    const handleOnPause = (nativeEvent: MediaPauseEvent) => {
        let isGloballyAtEnd = false;
        if (nativeEvent.isOriginTrusted) {
            if (startPlayFromBeginning) setStartPlayFromBeginning(false);

            // check if we have reached the end of this specific video and is the same time as the maxDuration, if so then set the isPlaying to false
            const isLocallyEnded = ended || isLocallyAtEndZone(currentTime);
            isGloballyAtEnd = isGloballyAtEndZone(currentTime);

            if (isLocallyEnded && isGloballyAtEnd) {
                setIsPlayingGlobal(false);
            } else if (!isLocallyEnded) {
                setIsPlayingGlobal(false);
            }
        }
        if (isGloballyAtEnd) {
            if ((props.videoType === maxDurationVideoType || props.forMobileView) && !videosWatched) {
                setVideosWatched(true);
            }
        }
    };

    const handleOnMediaSeekingRequest = (seekTime: number) => {
        const player = playerRef.current;
        if (!player) return;

        const isSeekingToEndZone = isLocallyAtEndZone(seekTime);
        const localSeekTime = isSeekingToEndZone ? duration : seekTime;
        const globalSeekTime = isSeekingToEndZone ? maxDurationGlobal : seekTime;

        latestSeekTimeRef.current = localSeekTime;
        setSeekTimeGlobal(globalSeekTime);

        if (isRestartingRef.current) return;

        if (player.paused) {
            if (!isSeekingToEndZone) {
                if (player.state.ended) {
                    isRestartingRef.current = true;

                    player
                        .play(new Event('seekSyncPlay'))
                        .then(() => {
                            return player.pause(new Event('seekSyncPause'));
                        })
                        .then(() => {
                            setTimeout(() => {
                                remote.seek(latestSeekTimeRef.current);
                                isRestartingRef.current = false;
                            }, 100);
                        })
                        .catch(() => {
                            remote.seek(latestSeekTimeRef.current);
                            isRestartingRef.current = false;
                        });
                } else {
                    remote.seek(localSeekTime);
                }
            }
        }
    };

    const handleSliderOnMediaPlayRequest = (nativeEvent: MediaPlayRequestEvent) => {
        const slider = sliderRef.current;
        if (!slider) return;

        const sliderTime = slider.state.pointerRate * duration;

        if (isLocallyAtEndZone(sliderTime + 0.15)) {
            nativeEvent.preventDefault();
        }
    };

    const handleOnSeeked = (seekTime: number, nativeEvent: MediaSeekedEvent) => {
        if (nativeEvent.isOriginTrusted && !isSeekingRef.current) {
            setSeekTimeGlobal(seekTime);
        }
    };

    const handleOnSliderDragStart = () => {
        isSeekingRef.current = true;
    };

    const handleOnSliderDragEnd = () => {
        isSeekingRef.current = false;
    };

    // -------------Sync Global -> Local ---------------
    useEffect(() => {
        if (isSeekingRef.current || props.forMobileView) return;

        const player = playerRef.current;
        if (!player) return;

        if (paused && isPlayingGlobal) {
            if (startPlayFromBeginning) {
                remote.seek(0);
            }

            if (isLocallyAtEndZone(player.currentTime) && !allVideosEnded() && !startPlayFromBeginning) {
                return;
            }

            remote.play();
        }

        if (!paused && !isPlayingGlobal) {
            remote.pause();
        }
    }, [isPlayingGlobal]);

    useEffect(() => {
        if (isSeekingRef.current || props.forMobileView) return;

        const player = playerRef.current;
        if (!player) return;

        latestSeekTimeRef.current = seekTimeGlobal;

        if (isRestartingRef.current) return;

        if (Math.abs(player.currentTime - seekTimeGlobal) < 0.3) return;

        const isSeekingToEndZone = isLocallyAtEndZone(seekTimeGlobal);

        if (player.state.ended && !isSeekingToEndZone) {
            isRestartingRef.current = true;

            player
                .play(new Event('seekSyncPlay'))
                .then(() => player.pause(new Event('seekSyncPause')))
                .then(() => {
                    setTimeout(() => {
                        remote.seek(latestSeekTimeRef.current);
                        isRestartingRef.current = false;
                    }, 100);
                })
                .catch(() => {
                    remote.seek(latestSeekTimeRef.current);
                    isRestartingRef.current = false;
                });
        } else {
            remote.seek(seekTimeGlobal);
        }
    }, [seekTimeGlobal]);

    return (
        <Box
            sx={{
                position: 'relative',
                maxHeight: props.maxHeight ?? 'auto',
                '& [data-media-player]': {
                    maxHeight: props.maxHeight ?? 'auto',
                },
                '& [data-media-player] video': {
                    maxHeight: props.maxHeight ?? 'auto',
                },
                '& [data-media-player][data-layout="plyr"]:not([data-fullscreen])': {
                    borderRadius: '0 ',
                    maxHeight: props.maxHeight ?? 'auto',
                },
            }}
            className={styles.videoContainer}
        >
            {props.forMobileView && (props.isSwipingMobile || props.pauseVideoMobile) && (
                <div style={{ position: 'absolute', inset: 0, zIndex: 10 }} />
            )}
            <MediaPlayer
                ref={playerRef}
                src={props.src}
                title={props.title}
                muted
                loop={false}
                autoPlay={false}
                onPlay={handleOnPlay}
                onPause={handleOnPause}
                onSeeked={handleOnSeeked}
                playsInline
            >
                <MediaProvider
                    mediaProps={{
                        // @ts-expect-error: attribute not part of HTMLMediaElement but supported by <video></video>
                        controlsList: 'nodownload nofullscreen noremoteplayback',
                        disablePictureInPicture: true,
                        disableRemotePlayback: true,
                        onContextMenuCapture: (e) => e.preventDefault(),
                    }}
                />
                <PlyrLayout
                    clickToFullscreen={false}
                    icons={plyrLayoutIcons}
                    controls={['play-large', 'play', 'progress', 'current-time', 'duration']}
                    invertTime={false}
                    slots={{
                        timeSlider: (
                            <VideoTimeSliderControl
                                ref={sliderRef}
                                pauseWhileDragging={true}
                                seekingRequestThrottle={100}
                                onDragStart={handleOnSliderDragStart}
                                onDragEnd={handleOnSliderDragEnd}
                                onMediaSeekingRequest={handleOnMediaSeekingRequest}
                                onMediaPlayRequest={handleSliderOnMediaPlayRequest}
                            />
                        ),
                    }}
                />
            </MediaPlayer>
        </Box>
    );
};

export default VideoPlayer;
